import {
	Button,
	FormProvider,
	MainLayout,
	NotificationCard,
	OneColumnStack,
	RHFDateTextField,
	RHFSelect,
	RHFTextField,
	Section,
} from "@arp/react-ndbx";
import { yupResolver } from "@hookform/resolvers/yup";
import { Box, Fade, Typography } from "@mui/material";
import { format } from "date-fns";
import React, { useEffect, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import Page from "../../components/Page";
import {
	Application,
	TRACKIFY_EVENT,
	ccRetrieveContent,
} from "../../config/const/enums";
import { retrievePageDisclaimer } from "../../config/disclaimers/disclaimers";
import { ccSearchPeriod } from "../../config/dropdownSelectValues/selectValues";
import { CC_RETRIEVE_QUOTE_FIELDNAME } from "../../config/pageInfo/pageFieldName";
import { pageName } from "../../config/pageInfo/pageNames";
import { useGetRetrieveQuote } from "../../hooks/dataHooks/useGetRetrieveQuote";
import useAuth from "../../hooks/useAuth";
import { ccRetrieveSchema } from "../../schema/retrieveQuoteSchema";
import {
	areAllValuesEmptyNullOrUndefined,
	hasExpiredItem,
	hasNotExpiredItem,
	removeEmptyAndUndefinedValues,
} from "../../utilities/preQualificationUtils";
import RetrieveResultsTable from "../shared/retrieveQuote/RetrieveResultsTable";
import { pushEvent, trackifyPageChange } from "@allianz/tracking";

function RetrieveQuotePageCC() {
	const [buttonDisable, setButtonDisable] = useState(true);
	const [resetChildForm, setResetChildForm] = useState();
	const [retrieveQuotePayload, setRetrieveQuotePayload] = useState({});
	const [searchQuoteDetails, setSearchQuoteDetails] = useState();
	const [filteredQuoteDetails, setFilteredQuoteDetails] = useState();
	const { getOktaAccessToken } = useAuth();
	const { isLoading, data, error, isRefetching, refetch } =
		useGetRetrieveQuote(retrieveQuotePayload);

	const methods = useForm({
		resolver: yupResolver(ccRetrieveSchema),
		mode: "all",
		reValidateMode: "all",
	});

	const {
		handleSubmit,
		control,
		getValues,
		formState: { errors, isValid },
		reset,
		resetField,
		setValue,
	} = methods;

	const quoteNumberWatch = useWatch({
		control,
		name: CC_RETRIEVE_QUOTE_FIELDNAME.QUOTE_NUMBER,
	});
	const surnameWatch = useWatch({
		control,
		name: CC_RETRIEVE_QUOTE_FIELDNAME.SURNAME,
	});
	const mobileNumberWatch = useWatch({
		control,
		name: CC_RETRIEVE_QUOTE_FIELDNAME.PHONE_NUMBER,
	});
	const dateOfBirthWatch = useWatch({
		control,
		name: CC_RETRIEVE_QUOTE_FIELDNAME.DATE_OF_BIRTH,
	});

	useEffect(() => {
		const retrieveFormData = getValues();
		const { searchPeriod, ...actualFormData } = retrieveFormData;
		if (areAllValuesEmptyNullOrUndefined(actualFormData)) {
			setButtonDisable(true);
		} else {
			setButtonDisable(!isValid);
		}
	}, [
		isValid,
		quoteNumberWatch,
		surnameWatch,
		mobileNumberWatch,
		dateOfBirthWatch,
	]);

	useEffect(() => {
		trackifyPageChange(TRACKIFY_EVENT.RETRIEVE_QUOTE_CC);
		pushEvent("purchasePath", Application.CONTACT_CENTER, true);
	}, []);

	const onSubmit = async (data) => {
		try {
			if (!areAllValuesEmptyNullOrUndefined(data)) {
				let oktaAccessToken = await getOktaAccessToken();
				const { accessToken } = { ...oktaAccessToken };
				if (accessToken) {
					const sanitisedData = removeEmptyAndUndefinedValues(data);
					if (
						sanitisedData?.dateOfBirth &&
						sanitisedData?.dateOfBirth instanceof Date
					) {
						sanitisedData.dateOfBirth = format(
							sanitisedData.dateOfBirth,
							"dd/MM/yyyy"
						);
					}
					const payload = {
						...sanitisedData,
						accessToken: accessToken,
					};
					setRetrieveQuotePayload(payload);
					setTimeout(async () => {
						setButtonDisable(true);
						const retrieveResponse = await refetch();
						setButtonDisable(false);
						setSearchQuoteDetails(retrieveResponse.data);
					}, 100);
				} else {
					cancelSearchResults();
				}
			}
		} catch (ex) {
			console.error(ex);
		}
	};

	const cancelSearchResults = () => {
		reset();
		setSearchQuoteDetails(null);
		setFilteredQuoteDetails(null);
		setRetrieveQuotePayload({});
		resetField(CC_RETRIEVE_QUOTE_FIELDNAME.QUOTE_NUMBER);
		setValue(CC_RETRIEVE_QUOTE_FIELDNAME.QUOTE_NUMBER, "");
		resetField(CC_RETRIEVE_QUOTE_FIELDNAME.PHONE_NUMBER);
		setValue(CC_RETRIEVE_QUOTE_FIELDNAME.PHONE_NUMBER, "");
		resetField(CC_RETRIEVE_QUOTE_FIELDNAME.SURNAME);
		setValue(CC_RETRIEVE_QUOTE_FIELDNAME.SURNAME, "");
		resetField(CC_RETRIEVE_QUOTE_FIELDNAME.DATE_OF_BIRTH);
		setValue(CC_RETRIEVE_QUOTE_FIELDNAME.DATE_OF_BIRTH, undefined);
	};

	const changeRefineDuration = (event) => {
		const selectedValue = event.target.value;
		// adding a random number to that every time the refine dropdown is changed the form is reset, so that the useffect is called when the value is called.
		const randomNumber = Math.floor(Math.random() * 100) + 1;
		setResetChildForm(randomNumber);
		if (selectedValue === ccRetrieveContent.LAST_30_DAYS) {
			const hasNotExpireQuotes = hasNotExpiredItem(searchQuoteDetails);
			setFilteredQuoteDetails(hasNotExpireQuotes);
		} else if (selectedValue === ccRetrieveContent.BEYOND_30_DAYS) {
			const hasExpireQuotes = hasExpiredItem(searchQuoteDetails);
			setFilteredQuoteDetails(hasExpireQuotes);
		} else {
			setFilteredQuoteDetails(searchQuoteDetails);
		}
	};

	return (
		<>
			<Page
				title="Retrieve Quote Page"
				identifier={pageName.CC_RETRIEVE_QUOTE_PAGE}
			>
				<MainLayout
					maxWidth="lg"
					sx={{
						mt: 8,
						mb: 5,
						outline: "0px solid purple",
						minHeight: "100vh",
					}}
				>
					<Page title="Retrieve quote CC" identifier="retrieve-quote-cc">
						<FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
							<Section md={7} mt={8}>
								<Section textAlign="left" mb={3}>
									<Typography variant="h3-bold">
										{ccRetrieveContent.RETRIEVE_APPLICATION}
									</Typography>
								</Section>
								<Section textAlign="left" mb={4}>
									<Typography variant="p2">
										{ccRetrieveContent.ENTER_DETAILS}
									</Typography>
								</Section>
								<Section textAlign="left" mb={4}>
									<Typography variant="p2">
										{ccRetrieveContent.FILTER_CRITERIA}
									</Typography>
								</Section>

								<Section mb={5}>
									<RHFTextField
										name={CC_RETRIEVE_QUOTE_FIELDNAME.QUOTE_NUMBER}
										className="subvariant-text-input"
										variant="standard"
										label="Application/quote reference number"
										inputProps={{ style: { textTransform: "uppercase" } }}
										type="text"
									/>
								</Section>
								<Section mb={5}>
									<RHFTextField
										name={CC_RETRIEVE_QUOTE_FIELDNAME.PHONE_NUMBER}
										className="subvariant-text-input"
										variant="standard"
										label="Mobile number"
										type="text"
									/>
								</Section>
								<Section mb={5}>
									<RHFTextField
										name={CC_RETRIEVE_QUOTE_FIELDNAME.SURNAME}
										className="subvariant-text-input"
										variant="standard"
										label="Surname"
										type="text"
									/>
								</Section>
								<Section>
									<RHFDateTextField
										name={CC_RETRIEVE_QUOTE_FIELDNAME.DATE_OF_BIRTH}
										variant="standard"
										label="Date of birth"
										showErrorCard={true}
									/>
								</Section>
								{searchQuoteDetails && searchQuoteDetails.length === 0 && (
									<Section mb={4} mt={3}>
										<Fade in={true} timeout={500}>
											<Box>
												<NotificationCard
													cardType="error"
													title=""
													message={ccRetrieveContent.SEARCH_NO_RESULT}
												/>
											</Box>
										</Fade>
									</Section>
								)}
								{(!searchQuoteDetails ||
									(searchQuoteDetails && searchQuoteDetails.length === 0)) && (
									<OneColumnStack flexShrink={1} mb={7} mt={9}>
										<Button
											id="cc-processing-continue"
											ariaLabel="continue"
											variant="contained"
											color="primary"
											size="medium"
											disabled={buttonDisable}
											type="submit"
										>
											{ccRetrieveContent.CONTINUE}
										</Button>
									</OneColumnStack>
								)}

								{!searchQuoteDetails && (
									<Section textAlign="left" mb={10}>
										<Typography variant="p2">
											{retrievePageDisclaimer.DUTY_DISCLOSURE}
										</Typography>
									</Section>
								)}
							</Section>
							{searchQuoteDetails && searchQuoteDetails.length > 0 && (
								<Section md={11} mt={8}>
									<Section textAlign="left" mb={3}>
										<Typography variant="h3-bold">
											{ccRetrieveContent.SEARCH_RESULTS}
										</Typography>
									</Section>
									<Section mb={9}>
										<RHFSelect
											name="searchPeriod"
											label="Select a period"
											variant="standard"
											items={ccSearchPeriod}
											onChange={changeRefineDuration}
										/>
									</Section>
									<RetrieveResultsTable
										searchQuoteDetails={
											filteredQuoteDetails
												? filteredQuoteDetails
												: searchQuoteDetails
										}
										cancelSearchResults={cancelSearchResults}
										resetChildForm={resetChildForm}
									/>
								</Section>
							)}
						</FormProvider>
					</Page>
				</MainLayout>
			</Page>
		</>
	);
}

export default RetrieveQuotePageCC;
